import HttpRequest from '@/util/HttpRequest'
import { cached, invalidate } from '@/util/RequestCache'

const KEY_HOME_NOTIFICATION = 'home_notification'

/**
 * 서버별로 켜고 끄는 기능 목록. MaotConfig 에 이 키가 없거나 값을 읽지 못하면
 * 아무 기능도 켜지 않는다(= 숨김이 기본).
 *
 * 값은 아래 두 형태를 모두 허용한다.
 *   ["risk_reference", ...]            배열에 포함되어 있으면 켬
 *   { "risk_reference": true, ... }    해당 키가 참이면 켬
 *
 * ※ MaotConfig 에 등록할 때 is_public = 1 로 저장해야 한다.
 *   비공개 설정은 관리자(is_superuser)만 조회할 수 있어(GetConfigController)
 *   일반 사용자에게는 기능이 켜지지 않는다.
 */
const KEY_ENABLED_FEATURES = 'enabled_features'

// 위험성평가 D/B (표준 위험성평가 참고자료 조회)
export const FEATURE_RISK_REFERENCE = 'risk_reference'

// AI 음성 작성. 게시판별로 켜므로 `${FEATURE_AI_VOICE_WRITE}_${boardId}` 형태로 조회한다.
// 예) ai_voice_write_801
export const FEATURE_AI_VOICE_WRITE = 'ai_voice_write'

export default {
  namespaced: true,
  state: {
    homeNotification: {},
    // null = 아직 조회 전
    enabledFeatures: null
  },
  mutations: {
    LOAD_HOME_NOTIFICATION (state, data) {
      state.homeNotification = JSON.parse(data.config)
    },

    LOAD_ENABLED_FEATURES (state, data) {
      state.enabledFeatures = data
    },
  },
  actions: {
    // 결재 요청함 추가/수정
    async SAVE_HOME_NOTIFICATION ({commit}, params) {
      // boardSlug = '' 인 경우 모든 게시판에 대해서 가져온다.
      const option = {
        key: KEY_HOME_NOTIFICATION,
        config: JSON.stringify(params.config)
      }
      return new Promise((resolve, reject) => {
        HttpRequest.post('/api/v1/config', option)
          .then(response => {
            // 설정을 바꿨으므로 캐시된 설정값을 버린다
            invalidate('config:')

            commit('LOAD_HOME_NOTIFICATION', response)
            resolve(response)
          }).catch(err => reject(err))
      })
    },

    // 결재 요청함 불러오기
    async LOAD_HOME_NOTIFICATION ({commit}) {
      // boardSlug = '' 인 경우 모든 게시판에 대해서 가져온다.
      const option = {
        params: {
          key: KEY_HOME_NOTIFICATION
        }
      }
      return new Promise((resolve, reject) => {
        HttpRequest.get('/api/v1/config', option)
          .then(response => {
            commit('LOAD_HOME_NOTIFICATION', response)
            resolve(response)
          }).catch(err => reject(err))
      })
    },

    /**
     * 서버에서 켜져 있는 기능 목록을 조회한다.
     * 화면이 열릴 때마다 부르지 않도록 세션 중 한 번만 조회하고 store 에 담아둔다.
     *
     * @returns {Promise<boolean>} 해당 기능이 켜져 있으면 true
     */
    async IS_FEATURE_ENABLED ({ commit, state, dispatch }, feature) {
      let features = state.enabledFeatures

      if (features === null) {
        try {
          // 설정이 없는 서버가 대부분이라 404 가 정상이다. 오류 알럿을 띄우지 않는다.
          features = await dispatch('LOAD_CONFIG', { key: KEY_ENABLED_FEATURES, notWarn: true })
        } catch (e) {
          features = undefined
        }
        // 조회에 실패했거나 값이 없어도 재요청하지 않도록 빈 값으로 확정한다.
        features = features || []
        commit('LOAD_ENABLED_FEATURES', features)
      }

      if (Array.isArray(features)) return features.includes(feature)
      return Boolean(features[feature])
    },

    /**
     * MaotConfig 모델에서 조회한다
     *
     * @param {Object} params
     * @param {String} params.key
     * @param {Boolean} [params.notWarn] 값이 없는 것이 정상인 조회에 쓴다.
     *   설정이 없으면 서버가 404 를 주는데, 기본값으로 두면 공통 오류 알럿
     *   ("설정 정보를 찾을 수 없습니다")이 사용자에게 그대로 뜬다.
     */
    async LOAD_CONFIG ({}, params) {
      const option = {
        params: {
          key: params.key,
        }
      }

      // 서버 설정은 로그인 중에 바뀌지 않는다. 화면이 열릴 때마다 부르지 않도록
      // 키별로 캐시한다. (사이드 상단 이미지 2 건이 매 페이지 이동마다 조회되던 문제)
      return cached(`config:${params.key}`, () => HttpRequest
        .get(`/api/v1/config`, option, params.notWarn)
        .then(response => {
          try {
            return JSON.parse(response.config)
          } catch (e) {
            return undefined
          }
        }))
    },
  },
  getters: {}
}
